import { type KeyboardEvent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { transcribeAudio } from "@/api/transcribeAudio";
import { cn } from "@/shared/lib/cn";
import { useAutosizeTextarea } from "@/shared/lib/hooks/useAutosizeTextarea";
import { useVoiceInput } from "@/shared/lib/hooks/useVoiceInput";
import { Spinner, useToast } from "@/shared/ui";
import { useMyRunSkills } from "../hooks/useRunSkills";
import type { ComposerAttachment } from "../lib/attachments";
import { activeSlashToken, applySlashSelection, filterSkillsByToken } from "../lib/slashSkills";
import { ComposerAttachments } from "./ComposerAttachments";
import { SkillSlashMenu } from "./SkillSlashMenu";
import { imageFilesFromClipboard } from "@/shared/lib/hooks/useFilePaste";
import { GlobeIcon, MicIcon, PaperclipIcon, SendIcon, StopIcon } from "./icons";

/** Cap the slash typeahead so a long skill list never grows an unwieldy menu. */
const MAX_SLASH_RESULTS = 8;

export interface ComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onStop: () => void;
  isStreaming: boolean;
  modelId: string | null;
  webSearch: boolean;
  onToggleWebSearch: () => void;
  /** Web search is gated to paid plans — false disables the toggle for free users. */
  canWebSearch: boolean;
  /** Pending composer attachments (uploading/ready/error). */
  attachments: ComposerAttachment[];
  /** Queue picked/dropped files for upload. */
  onAddFiles: (files: File[]) => void;
  /** Remove a pending attachment. */
  onRemoveAttachment: (localId: string) => void;
  /** True while any attachment is still uploading — blocks send. */
  attachmentsUploading: boolean;
  variant?: "welcome" | "dock";
  placeholder?: string;
}

/**
 * The pill composer (DESIGN §5): auto-growing textarea over a control row with
 * the model picker and a send/stop button. Enter sends, Shift+Enter newlines.
 * Two variants — the welcome pill (28px radius) and the docked reply pill (24px).
 */
export function Composer({
  value,
  onChange,
  onSubmit,
  onStop,
  isStreaming,
  modelId,
  webSearch,
  onToggleWebSearch,
  canWebSearch,
  attachments,
  onAddFiles,
  onRemoveAttachment,
  attachmentsUploading,
  variant = "welcome",
  placeholder,
}: ComposerProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useAutosizeTextarea(textareaRef, value);

  const voice = useVoiceInput({
    transcribe: transcribeAudio,
    onTranscript: (text) => onChange(value.trim() ? `${value.trimEnd()} ${text}` : text),
    onError: (kind) =>
      toast(kind === "permission" ? t("chat.voice.permissionDenied") : t("chat.voice.failed")),
  });

  // Slash typeahead: while the draft opens with `/`, offer the user's skills
  // filtered by the typed token. Selecting one scopes the next turn to it —
  // the actual resolve + strip happens on send in `ChatView`.
  const { data: mySkills } = useMyRunSkills();
  const [slashOpen, setSlashOpen] = useState(true);
  const [slashIndex, setSlashIndex] = useState(0);
  const slashToken = activeSlashToken(value);
  const slashMatches = useMemo(() => {
    if (slashToken === null) return [];
    return filterSkillsByToken(mySkills ?? [], slashToken).slice(0, MAX_SLASH_RESULTS);
  }, [mySkills, slashToken]);
  const showSlash = slashOpen && slashToken !== null && slashMatches.length > 0;

  function changeDraft(next: string) {
    // Reopen + reset the highlight whenever a slash command is being typed.
    if (next.startsWith("/")) {
      setSlashOpen(true);
      setSlashIndex(0);
    }
    onChange(next);
  }

  function chooseSlash(index: number) {
    const skill = slashMatches[index];
    if (!skill) return;
    changeDraft(applySlashSelection(value, skill.name));
    setSlashOpen(false);
    textareaRef.current?.focus();
  }

  const hasReadyAttachment = attachments.some((a) => a.status === "ready");
  const canSend =
    (value.trim().length > 0 || hasReadyAttachment) && modelId !== null && !attachmentsUploading;
  const dock = variant === "dock";

  function submit() {
    // Mid-stream submits queue the draft (the provider drains it when the
    // run ends) — only an empty/blocked draft is refused.
    if (!canSend) return;
    onSubmit();
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (showSlash) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSlashIndex((i) => (i + 1) % slashMatches.length);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setSlashIndex((i) => (i - 1 + slashMatches.length) % slashMatches.length);
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        chooseSlash(slashIndex);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setSlashOpen(false);
        return;
      }
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  }

  function pickFiles(list: FileList | null) {
    const files = list ? Array.from(list) : [];
    if (files.length > 0) onAddFiles(files);
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      className={cn(
        "relative bg-bubble shadow-[var(--shadow-soft)]",
        dock ? "rounded-[24px] px-3 pb-2 pt-3" : "rounded-pill px-4 pb-3 pt-4",
      )}
    >
      {showSlash ? (
        <SkillSlashMenu
          skills={slashMatches}
          activeIndex={slashIndex}
          onHover={setSlashIndex}
          onSelect={(skill) => chooseSlash(slashMatches.indexOf(skill))}
        />
      ) : null}
      <ComposerAttachments attachments={attachments} onRemove={onRemoveAttachment} />
      <textarea
        ref={textareaRef}
        data-testid="chat-composer"
        rows={1}
        value={value}
        onChange={(event) => changeDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onPaste={(event) => {
          const files = imageFilesFromClipboard(event.clipboardData);
          if (files.length) {
            event.preventDefault();
            event.nativeEvent.stopImmediatePropagation();
            onAddFiles(files);
          }
        }}
        placeholder={placeholder ?? t("chat.composer.placeholder")}
        aria-label={t("chat.composer.placeholder")}
        className="block max-h-[200px] w-full resize-none bg-transparent px-1 pb-1 font-sans text-[16px] leading-[1.5] text-ink outline-none placeholder:text-ink-faint"
      />
      <input
        ref={fileInputRef}
        data-testid="chat-file-input"
        type="file"
        multiple
        className="hidden"
        onChange={(event) => {
          pickFiles(event.target.files);
          event.target.value = "";
        }}
      />
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          aria-label={t("chat.attachments.add")}
          title={t("chat.attachments.add")}
          className="grid size-[34px] shrink-0 place-items-center rounded-pill text-ink outline-none transition-colors duration-150 ease-house hover:bg-tint focus-visible:bg-tint [&_svg]:size-[17px]"
        >
          <PaperclipIcon />
        </button>
        <button
          type="button"
          onClick={onToggleWebSearch}
          disabled={!canWebSearch}
          aria-pressed={canWebSearch && webSearch}
          aria-label={t("chat.webSearch")}
          title={canWebSearch ? undefined : t("chat.webSearchProOnly")}
          className={cn(
            "inline-flex h-[34px] items-center gap-2 rounded-pill px-3 font-sans text-[13px] font-medium outline-none transition-colors duration-150 ease-house focus-visible:bg-tint [&_svg]:size-[15px]",
            !canWebSearch
              ? "cursor-not-allowed text-ink-faint"
              : webSearch
                ? "bg-accent text-accent-ink"
                : "text-ink hover:bg-tint",
          )}
        >
          <GlobeIcon />
          <span className="max-[520px]:hidden">{t("chat.webSearch")}</span>
        </button>
        <div className="flex-1" />
        {voice.supported ? (
          <button
            type="button"
            onClick={voice.toggle}
            disabled={isStreaming || voice.status === "transcribing"}
            aria-pressed={voice.status === "recording"}
            aria-label={t(voice.status === "recording" ? "chat.voice.stop" : "chat.voice.start")}
            title={t(voice.status === "recording" ? "chat.voice.stop" : "chat.voice.start")}
            className={cn(
              "grid size-[34px] shrink-0 place-items-center rounded-pill outline-none transition-colors duration-150 ease-house focus-visible:bg-tint disabled:opacity-50 [&_svg]:size-[17px]",
              voice.status === "recording"
                ? "animate-pulse bg-accent text-accent-ink"
                : "text-ink hover:bg-tint",
            )}
          >
            {voice.status === "transcribing" ? <Spinner size={16} /> : <MicIcon />}
          </button>
        ) : null}
        {isStreaming ? (
          <div className="flex items-center gap-2">
            {canSend ? (
              <button
                type="submit"
                data-testid="chat-send-button"
                aria-label={t("chat.queueMessage")}
                title={t("chat.queueMessage")}
                className="grid size-10 place-items-center rounded-full border border-accent bg-transparent text-accent outline-none transition-transform duration-150 ease-house hover:bg-accent/10 focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring)] active:scale-95"
              >
                <SendIcon className="size-[18px]" />
              </button>
            ) : null}
            <button
              type="button"
              data-testid="chat-stop-button"
              onClick={onStop}
              aria-label={t("chat.stopResponse")}
              className="grid size-10 place-items-center rounded-full bg-ink text-paper outline-none transition-transform duration-150 ease-house hover:bg-ink-strong focus-visible:ring-[3px] focus-visible:ring-tint active:scale-95"
            >
              <StopIcon className="size-4" />
            </button>
          </div>
        ) : (
          <button
            type="submit"
            data-testid="chat-send-button"
            disabled={!canSend}
            aria-label={t("chat.sendMessage")}
            className="grid size-10 place-items-center rounded-full bg-accent text-accent-ink outline-none transition-[background,transform] duration-150 ease-house hover:bg-accent-hover focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring)] active:scale-95 disabled:bg-line disabled:text-ink-faint"
          >
            <SendIcon className="size-[18px]" />
          </button>
        )}
      </div>
    </form>
  );
}
